import Constants from 'expo-constants';

import { AppConfig } from '../../../app.config';
import { getDynamicAppConfig } from '../../../get-dynamic-app-config';

import { SyncStorageKey, syncStorage } from './sync-storage';
import { RBIEnv, Region } from './types';

// following keys are not defined in app.config.ts but are used in the app
// you can remove them from this definition to see where they are used
type UndefinedConfigKey = 'bringg_realtime' | 'version' | 'key';

export type ConfigKey = keyof AppConfig | UndefinedConfigKey;
export interface IGetConfigValueProps<K extends ConfigKey> {
  key: K;
  defaultValue?: any;
  disallowEnvPickerValue?: boolean;
}

type EnvConfig = {
  env: RBIEnv;
  buildRegion: Region;
};

export function getConfigValue<K extends ConfigKey>({
  key,
  defaultValue,
  disallowEnvPickerValue = false,
}: IGetConfigValueProps<K>): K extends keyof AppConfig ? AppConfig[K] : any {
  const configDataLocationOne = Constants.manifest2?.extra?.expoClient?.extra?.eas;
  const configDataLocationTwo = Constants.expoConfig?.extra?.eas;

  let actualValue = null;
  const derivedPickedEnv = disallowEnvPickerValue || !allowEnvPicker ? null : pickedEnv; // in real prod the disallowed env picker is never be used

  actualValue =
    derivedPickedEnv?.[key] ?? configDataLocationOne?.[key] ?? configDataLocationTwo?.[key];
  const result = actualValue || defaultValue;
  return result;
}

export const allowEnvPicker = getConfigValue({
  key: 'allowEnvPicker',
  disallowEnvPickerValue: true,
});

export const getBakedInEnvConfig = () => {
  return {
    env: getConfigValue({ key: 'env', disallowEnvPickerValue: true }) as RBIEnv,
    buildRegion: getConfigValue({ key: 'buildRegion', disallowEnvPickerValue: true }) as Region,
  };
};

const pickedEnv: any = (() => {
  if (!allowEnvPicker) {
    return null;
  }

  const pickedEnvConfig = syncStorage.getObject(
    SyncStorageKey.INTERNAL_BUILD_ONLY_ENV_PICKER_CONFIG
  ) as EnvConfig;

  if (!pickedEnvConfig) {
    return null;
  }

  const defaults = getBakedInEnvConfig(); // handles legacy use cases without breaking existing internal users

  return getDynamicAppConfig(
    pickedEnvConfig?.buildRegion || defaults.buildRegion,
    pickedEnvConfig?.env || defaults.env
  );
})();

export const shouldShowEnvPickerAtStart = () => allowEnvPicker && !pickedEnv;

export const setPickedEnvConfig = (envConfig: EnvConfig) =>
  syncStorage.setObject(SyncStorageKey.INTERNAL_BUILD_ONLY_ENV_PICKER_CONFIG, envConfig);
